/* eslint-disable react/no-danger */
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useQuery, useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import {
  downloadFileReport,
  downloadReportPdfFile,
  downloadReportDocxFile,
  downloadZipFile,
} from 'apis/summaryReport.api';
import { getProjectById } from 'apis/projects.api';
import { TOAST } from 'constant';
import {
  setExpenditureTableOfSummaryReport,
  formatFileName,
  formatCurrency,
  formatDate,
  getProductDetail,
  formatFileNameProduct,
  getAcadamicLevelShortHand,
  toNonAccentVietnamese,
  downloadOrViewPdfFile,
} from 'utils/func';
import { toast } from 'layout';
import RichTextViewer from 'components/RichTextViewer';
import DownloadOrViewPdf from 'components/DownloadOrViewPdf';
import { PRODUCT_TYPE } from 'constant/summary-report.constant';
import GeneralInfo from '../ReportFormR05/GeneralInfo';
import './styles.scss';
import UploadMultipleFile from '../ReportFormR05/UploadMultipleFile';
import ManagementAppendix from '../ManagementAppendix';

export default function ReviewForm({ summaryReport, projectId, setIsViewHistoryVisible }) {
  const pathname = useLocation()?.pathname || '';

  const { t } = useTranslation();

  const { control } = useForm({ mode: 'onChange' });
  const { errors } = useFormState({ control });

  const [summaryReportAppendixeArray, setSummaryReportAppendixeArray] = useState([]);

  const summaryReportId = useMemo(() => summaryReport?.summaryReport?.id || null, [summaryReport]);

  const summaryReportRevises = useMemo(
    () => summaryReport?.summaryReport?.summaryReportRevises ?? [],
    [summaryReport]
  );

  const projectCode = useMemo(
    () => summaryReport?.summaryReport?.project?.projectCode || '',
    [summaryReport]
  );

  const expenditureFileUrl = useMemo(
    () => summaryReport?.summaryReport?.expenditureFileUrl || null,
    [summaryReport]
  );

  const summaryReportFinishedTasks = useMemo(
    () => summaryReport?.summaryReport?.summaryReportFinishedTasks ?? [],
    [summaryReport]
  );

  const summaryReportUnfinishedTasks = useMemo(
    () => summaryReport?.summaryReport?.summaryReportUnfinishedTasks ?? [],
    [summaryReport]
  );

  const softProducts = useMemo(
    () => summaryReport?.summaryReportProducts?.scientificPublications?.softProducts ?? [],
    [summaryReport]
  );

  const hardProducts = useMemo(
    () => summaryReport?.summaryReportProducts?.scientificPublications?.hardProducts ?? [],
    [summaryReport]
  );

  const scientificPublications = useMemo(
    () =>
      summaryReport?.summaryReportProducts?.scientificPublications?.scientificPublications ?? [],
    [summaryReport]
  );

  const intellectualPropertys = useMemo(
    () => summaryReport?.summaryReportProducts?.intellectualPropertys ?? [],
    [summaryReport]
  );

  const transferredTechnologies = useMemo(
    () => summaryReport?.summaryReport?.summaryReportTransferredTechnologies ?? [],
    [summaryReport]
  );

  const trainingResults = useMemo(
    () => summaryReport?.summaryReportProducts?.trainingResults ?? [],
    [summaryReport]
  );

  const summaryReportConferences = useMemo(
    () => summaryReport?.summaryReport?.summaryReportConferences ?? [],
    [summaryReport]
  );

  const summaryReportParticipants = useMemo(
    () => summaryReport?.summaryReport?.summaryReportParticipants ?? [],
    [summaryReport]
  );

  const summaryReportExpenditures = useMemo(
    () =>
      summaryReport?.summaryReport?.summaryReportExpenditures?.filter((item) => item?.no === 0) ??
      [],
    [summaryReport]
  );

  const subSummaryReportExpenditures = useMemo(
    () =>
      summaryReport?.summaryReport?.summaryReportExpenditures
        ?.filter((item) => item?.no !== 0)
        .sort((a, b) => a.no - b.no) ?? [],
    [summaryReport]
  );

  const formatTitle = useCallback(() => {
    if (pathname.includes('midterm-document')) return 'midterm';
    if (pathname.includes('acceptance-document')) return 'acceptance';
    if (pathname.includes('liquidation-document')) return 'liquidation';
    return 'summary';
  }, [pathname]);

  const formatTitleZip = () => {
    const code = summaryReport?.summaryReport?.summaryReportType?.code;
    if (code === 'midtermDocument') return 'Bao cao giua ky';
    if (code === 'acceptanceDocument') return 'Bao cao nghiem thu';
    if (code === 'liquidationDocument') return 'Bao cao thanh ly';
    return 'Bao cao';
  };

  const { data: _projectData } = useQuery(['getProjectById', summaryReport?.projectId], () =>
    getProjectById(projectId)
  );

  const projectData = useMemo(() => _projectData?.data, [_projectData]);

  const {
    refetch: handleDownloadPdfFile,
    isLoading: isDownloadPdfFileLoading,
    isFetching: isDownloadPdfFileFetching,
  } = useQuery(['downloadProjectR05PdfFile'], () => downloadReportPdfFile(summaryReportId), {
    enabled: false,
    onSuccess: (res) => {
      if (res?.data) {
        const file = new Blob([res.data]);
        const filename = res.headers['content-disposition'].split('filename=')[1];
        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = filename;
        link.click();
      }
    },
    onError: () => {
      toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
    },
  });

  const { mutateAsync: mutateAsyncDownloadFile } = useMutation(downloadFileReport);

  const handleDownloadFile = async (opts, fileNameFormat = '', isView = false, isPdf = true) => {
    try {
      const res = await mutateAsyncDownloadFile({ path: opts.path, folder: opts?.folder || '' });
      if (res?.data) {
        const type = isPdf ? 'application/pdf' : 'null';
        const file = new Blob([res.data], { type });
        const filename =
          res.headers['content-disposition']?.split('filename=')?.[1] || fileNameFormat;
        downloadOrViewPdfFile(file, filename, isView);
      }
    } catch (error) {
      if (isView) {
        toast(TOAST.ERROR, t('projectProposal.review.viewPdfErrorTitle'));
      } else {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      }
    }
  };

  const {
    refetch: handleDownloadDocxFile,
    isLoading: isDownloadDocxFileLoading,
    isFetching: isDownloadDocxFileFetching,
  } = useQuery(['downloadProjectR05DocxFile'], () => downloadReportDocxFile(summaryReportId), {
    enabled: false,
    onSuccess: (res) => {
      if (res?.data) {
        const file = new Blob([res.data]);
        const filename = res.headers['content-disposition'].split('filename=')[1];
        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = filename;
        link.click();
      }
    },
    onError: () => {
      toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
    },
  });

  const {
    refetch: handleDownloadZipFile,
    isLoading: isDownloadZipFileLoading,
    isFetching: isDownloadZipFileFetching,
  } = useQuery(
    ['downloadProjectR05ZipFile', summaryReportId],
    () => downloadZipFile(summaryReportId),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const topicCode = summaryReport?.summaryReport?.project?.topicCode;
          const academicRank =
            summaryReport?.summaryReport?.project?.projectProposal?.owner?.contacts?.[0]
              ?.scientificProfile?.academicRank;
          const academicDegree =
            summaryReport?.summaryReport?.project?.projectProposal?.owner?.contacts?.[0]
              ?.scientificProfile?.academicDegree;

          const academicLevelShorthand = getAcadamicLevelShortHand(
            academicRank,
            academicDegree,
            t,
            true
          );
          const ownerName =
            summaryReport?.summaryReport?.project?.projectProposal?.owner?.contacts?.[0]
              ?.fullname || '';
          const fileName = `${topicCode} - ${academicLevelShorthand}${toNonAccentVietnamese(
            ownerName
          )} - ${formatTitleZip()}.zip`;
          const file = new Blob([res.data]);
          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = fileName;
          link.click();
        }
      },
    }
  );

  useEffect(() => {
    setSummaryReportAppendixeArray(summaryReport?.summaryReportAppendixes || []);
  }, [JSON.stringify(summaryReport)]);

  const renderDownloadExpenditureFile = useCallback(() => {
    if (!expenditureFileUrl) return null;
    if (expenditureFileUrl.includes('pdf')) {
      return (
        <DownloadOrViewPdf
          opts={{ path: expenditureFileUrl }}
          fileName={formatFileName(expenditureFileUrl)}
          handleDownloadOrView={handleDownloadFile}
        />
      );
    }
    return (
      <span
        className="p-button p-button-text p-button-sm mt-1 mb-2 p-0"
        onClick={() =>
          handleDownloadFile(
            { path: expenditureFileUrl },
            formatFileName(expenditureFileUrl),
            false,
            false
          )
        }
      >
        <i className="pi pi-file-excel text-green-500 text-xl" />{' '}
        <span className="text-blue-500 underline font-italic text-base">
          {formatFileName(expenditureFileUrl)}
        </span>
      </span>
    );
  }, [expenditureFileUrl]);
  return (
    <div className="review-form">
      <div className="flex justify-content-end mb-2">
        {summaryReportRevises?.length > 0 && (
          <Button
            type="button"
            label={t('submitDocument.viewHistory')}
            icon="pi pi-history"
            className="block w-auto h-3rem mr-4 bg-cyan-500 border-none hover:bg-cyan-600"
            onClick={() => setIsViewHistoryVisible(true)}
          />
        )}
        <Button
          type="button"
          label={t('projectProposal.review.exportZip')}
          icon={`pi ${
            isDownloadZipFileLoading || isDownloadZipFileFetching ? 'pi-spin pi-spinner' : 'pi-file'
          }`}
          className="block w-auto h-3rem mr-4"
          onClick={handleDownloadZipFile}
          disabled={
            !summaryReport?.summaryReport || isDownloadZipFileLoading || isDownloadZipFileFetching
          }
        />

        <Button
          type="button"
          label={t('projectProposal.review.exportReportDocx')}
          icon={`pi ${
            isDownloadDocxFileLoading || isDownloadDocxFileFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file-word'
          }`}
          className="block w-auto h-3rem mr-4"
          severity="info"
          onClick={handleDownloadDocxFile}
          disabled={
            !summaryReport?.summaryReport || isDownloadDocxFileLoading || isDownloadDocxFileFetching
          }
        />
        <Button
          type="button"
          label={t('projectProposal.review.exportReportPdf')}
          icon={`pi ${
            isDownloadPdfFileLoading || isDownloadPdfFileFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file-pdf'
          }`}
          className="block w-auto h-3rem border-none"
          style={{ backgroundColor: 'var(--red-400)' }}
          onClick={handleDownloadPdfFile}
          disabled={
            !summaryReport?.summaryReport || isDownloadPdfFileLoading || isDownloadPdfFileFetching
          }
        />
      </div>
      <div className="p-5">
        <div className="mb-5 px-5 flex justify-content-between align-items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="VNU-HCM logo"
            height="60px"
            className="ml-3"
          />
          <div className="mr-8">
            <span>{t('r05.formR05')}:</span>
            <br />
            <span>{t('r05.projectCode')}:</span>
          </div>
        </div>
        <div className="mb-5">
          <div className="text-center">
            <p className="font-bold text-2xl my-0">{t(`r05.title.${formatTitle()}`)}</p>
            <p className="font-bold text-2xl my-0">{t('r05.topic')}</p>
            <p className="font-italic">{t('r05.note')}</p>
          </div>
        </div>
        <form autoComplete="off" className="p-fluid form-midterm-container">
          <GeneralInfo
            summaryReportAllocatedExpenditures={
              summaryReport?.summaryReport?.summaryReportAllocatedExpenditures
            }
            projectCode={projectCode}
            handleDownloadFile={handleDownloadFile}
          />

          <div>
            <h4 className="my-2">{t('r05.b.title')}</h4>
            <h4 className="my-1">{t('r05.b.b1.title')}</h4>
            {/* finished tasks */}
            <h6 className="my-2 font-bold">{t('r05.b.b1.no1.title')}</h6>
            <div className="form-table">
              <table className="mb-2">
                <thead>
                  <tr>
                    <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                      {t('formLayout.numericalOrder')}
                    </th>
                    <th className="text-center" style={{ minWidth: 220, width: '33.33%' }}>
                      {t('r05.b.b1.no1.registrationContent')}
                    </th>
                    <th className="text-center" style={{ minWidth: 220, width: '33.33%' }}>
                      {t('r05.b.b1.no1.result')}
                    </th>
                    <th className="text-center" style={{ minWidth: 220, width: '33.33%' }}>
                      {t('r05.b.b1.no1.completionLevel')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summaryReportFinishedTasks.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-2">
                        {t('submitDocument.noContent')}
                      </td>
                    </tr>
                  )}
                  {summaryReportFinishedTasks?.map((item, index) => (
                    <tr key={item?.id}>
                      <td className="text-center">{index + 1}</td>
                      <td className="text-center">{item?.content}</td>
                      <td className="text-center">{item?.result}</td>
                      <td className="text-center">{item?.completionLevel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* unfinished tasks */}
            <h6 className="my-2">{t('r05.b.b1.no2.title')}</h6>
            <div className="form-table">
              <table className="mb-2">
                <thead>
                  <tr>
                    <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                      {t('formLayout.numericalOrder')}
                    </th>
                    <th className="text-center" style={{ minWidth: 220, width: '33.33%' }}>
                      {t('r05.b.b1.no2.incompleteContent')}
                    </th>
                    <th className="text-center" style={{ minWidth: 220, width: '33.33%' }}>
                      {t('r05.b.b1.no2.cause')}
                    </th>
                    <th className="text-center" style={{ minWidth: 220, width: '33.33%' }}>
                      {t('r05.b.b1.no2.remedies')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summaryReportUnfinishedTasks.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-2">
                        {t('submitDocument.noContent')}
                      </td>
                    </tr>
                  )}
                  {summaryReportUnfinishedTasks?.map((item, index) => (
                    <tr key={item?.id}>
                      <td className="text-center">{index + 1}</td>
                      <td className="text-wrap">{item?.content}</td>
                      <td className="text-wrap">{item?.reason}</td>
                      <td className="text-wrap">{item?.solution}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <h4 className="mt-4 mb-2">{t('r05.b.b2.title')}</h4>
          {/* soft product */}
          <h4 className="mt-2">
            {t('projectProposal.researchResult.productAndResearchResultDes.softProduct.label')}
          </h4>
          <p>
            ({t('projectProposal.researchResult.productAndResearchResultDes.softProduct.note')})
          </p>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 200 }}>
                    {t('submitDocument.product.productDetail.name')}
                  </th>
                  <th className="text-center" style={{ minWidth: 220, width: '33.33%' }}>
                    {t('submitDocument.product.productDetail.evaluationCriteria')}
                  </th>
                  <th className="text-center" style={{ minWidth: 220, width: '33.33%' }}>
                    {t('submitDocument.product.productDetail.note')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 200 }}>
                    {t('r05.b.b2.table.proof')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {softProducts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-2">
                      {t('submitDocument.noProduct')}
                    </td>
                  </tr>
                )}
                {softProducts?.map((item, index) => (
                  <tr key={item.customId}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item?.productDetail?.name}</td>
                    <td>{item?.productDetail?.evaluativeCriteria}</td>
                    <td>{item?.productDetail?.note}</td>
                    <td>
                      {item?.fileUrl && (
                        <DownloadOrViewPdf
                          opts={{ path: item?.fileUrl }}
                          fileName={formatFileNameProduct(
                            item?.fileUrl,
                            projectCode,
                            PRODUCT_TYPE.SOFT_PRODUCT.short,
                            index + 1
                          )}
                          handleDownloadOrView={handleDownloadFile}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* hard product */}
          <h4>
            {t('projectProposal.researchResult.productAndResearchResultDes.hardProduct.label')}
          </h4>
          <p>
            ({t('projectProposal.researchResult.productAndResearchResultDes.hardProduct.note')})
          </p>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th rowSpan={3} className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th rowSpan={3} className="text-center" style={{ minWidth: 220, width: 220 }}>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.productName'
                    )}
                  </th>
                  <th rowSpan={3} className="text-center" style={{ minWidth: 200, width: 200 }}>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.qualityCriteria'
                    )}
                  </th>
                  <th rowSpan={3} className="text-center" style={{ minWidth: 80, width: 80 }}>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.measure'
                    )}
                  </th>
                  <th colSpan={3} className="text-center" style={{ minWidth: 330, width: '100%' }}>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.qualityLevel'
                    )}
                  </th>
                  <th rowSpan={3} className="text-center" style={{ minWidth: 100, width: 100 }}>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.expectedQuantity'
                    )}
                  </th>
                  <th rowSpan={3} className="text-center" style={{ minWidth: 100, width: 100 }}>
                    {t('r05.b.b2.table.proof')}
                  </th>
                </tr>
                <tr>
                  <th rowSpan={2} style={{ minWidth: 80, width: 80 }}>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.evaluativeCriteria'
                    )}
                  </th>
                  <th colSpan={2}>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.similarModel'
                    )}
                  </th>
                </tr>
                <tr>
                  <th>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.domestic'
                    )}
                  </th>
                  <th>
                    {t(
                      'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.international'
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {hardProducts.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-2">
                      {t('submitDocument.noProduct')}
                    </td>
                  </tr>
                )}
                {hardProducts?.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item?.productDetail?.name}</td>
                    <td>{item?.productDetail?.qualityCriteria}</td>
                    <td>{item?.productDetail?.measure}</td>
                    <td>{item?.productDetail?.evaluativeCriteria}</td>
                    <td>{item?.productDetail?.domesticModelQuality}</td>
                    <td>{item?.productDetail?.internationalModelQuality}</td>
                    <td>{item?.productDetail?.expectedQuantity}</td>
                    <td>
                      {item?.fileUrl && (
                        <DownloadOrViewPdf
                          opts={{ path: item?.fileUrl }}
                          fileName={formatFileNameProduct(
                            item?.fileUrl,
                            projectCode,
                            PRODUCT_TYPE.HARD_PRODUCT.short,
                            index + 1
                          )}
                          handleDownloadOrView={handleDownloadFile}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h4 className="mt-4">{t('r05.b.b2.no1')}</h4>
          {/* scientific publications */}
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 200 }}>
                    {t('r05.b.b2.table.productName')}
                  </th>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('r05.b.b2.table.kpiType')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 200 }}>
                    {t('r05.b.b2.table.kpiName')}
                  </th>
                  <th className="text-center" style={{ minWidth: 250, width: 250 }}>
                    {t('r05.b.b2.table.productDetail')}
                  </th>
                  <th className="text-center" style={{ minWidth: 220, width: 220 }}>
                    {t('r05.b.b2.table.proof')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {scientificPublications.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-2">
                      {t('submitDocument.noScientificPublication')}
                    </td>
                  </tr>
                )}
                {scientificPublications?.length > 0 &&
                  scientificPublications.map((item, index) => (
                    <tr key={item?.customId ?? item?.id}>
                      <td className="text-center">{index + 1}</td>
                      <td>{item?.productDetail?.title}</td>
                      <td>{item?.researchKpiGroup?.name}</td>
                      <td>{item?.researchKpi?.name}</td>
                      <td
                        dangerouslySetInnerHTML={{
                          __html: getProductDetail(t, item?.productDetail),
                        }}
                      />
                      <td>
                        {item?.fileUrl && (
                          <DownloadOrViewPdf
                            opts={{ path: item?.fileUrl }}
                            fileName={formatFileNameProduct(
                              item?.fileUrl,
                              projectCode,
                              PRODUCT_TYPE.SCIENTIFIC_PUBLICATION.short,
                              index + 1
                            )}
                            handleDownloadOrView={handleDownloadFile}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* intellectual property */}
          <h4>{t('r05.b.b2.no2')}</h4>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 160, width: 200 }}>
                    {t('r05.b.b2.table.productName')}
                  </th>
                  <th className="text-center" style={{ minWidth: 60, width: 60 }}>
                    {t('r05.b.b2.table.kpiType')}
                  </th>
                  <th className="text-center" style={{ minWidth: 160, width: 200 }}>
                    {t('r05.b.b2.table.kpiName')}
                  </th>
                  <th className="text-center" style={{ minWidth: 160, width: 200 }}>
                    {t('r05.b.b2.table.note')}
                  </th>
                  <th className="text-center" style={{ minWidth: 160, width: 200 }}>
                    {t('r05.b.b2.table.proof')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {intellectualPropertys.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-2">
                      {t('submitDocument.noProduct')}
                    </td>
                  </tr>
                )}
                {intellectualPropertys?.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item?.productDetail?.title}</td>
                    <td>{item?.researchKpiGroup?.name}</td>
                    <td>{item?.researchKpi?.name}</td>
                    <td
                      dangerouslySetInnerHTML={{
                        __html: getProductDetail(t, item?.productDetail),
                      }}
                    />
                    <td>
                      {item?.fileUrl && (
                        <DownloadOrViewPdf
                          opts={{ path: item?.fileUrl }}
                          fileName={formatFileNameProduct(
                            item?.fileUrl,
                            projectCode,
                            PRODUCT_TYPE.INTELLECTUAL_PROPERTY.short,
                            index + 1
                          )}
                          handleDownloadOrView={handleDownloadFile}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* transfer technology  */}
          <h4>{t('r05.b.b2.technologyOrSolution.title')}</h4>
          <div className="form-table p-fluid">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 180, width: 200 }}>
                    {t('r05.b.b2.technologyOrSolution.name')}
                  </th>
                  <th className="text-center" style={{ minWidth: 60, width: 60 }}>
                    {t('r05.b.b2.technologyOrSolution.year')}
                  </th>
                  <th className="text-center" style={{ minWidth: 140, width: 140 }}>
                    {t('r05.b.b2.technologyOrSolution.partner')}
                  </th>
                  <th className="text-center" style={{ minWidth: 80, width: 100 }}>
                    {t('r05.b.b2.technologyOrSolution.date')}
                  </th>
                  <th className="text-center" style={{ minWidth: 100, width: 100 }}>
                    {t('r05.b.b2.technologyOrSolution.revenue')}
                  </th>
                  <th className="text-center" style={{ minWidth: 140, width: 180 }}>
                    {t('r05.b.b2.technologyOrSolution.scale')}
                  </th>
                  <th className="text-center" style={{ minWidth: 140, width: 160 }}>
                    {t('r05.b.b2.table.proof')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {transferredTechnologies.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-2">
                      {t('submitDocument.noTransferTechnology')}
                    </td>
                  </tr>
                )}
                {transferredTechnologies?.map((item, index) => (
                  <tr key={item.customId}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.transferredYear}</td>
                    <td>{item.partner}</td>
                    <td>{formatDate(item.contractedDate)}</td>
                    <td className="text-right">{formatCurrency(item.revenue)}</td>
                    <td>{item.scale}</td>
                    <td>
                      {item?.fileUrl && (
                        <DownloadOrViewPdf
                          opts={{ path: item?.fileUrl }}
                          fileName={formatFileNameProduct(
                            item?.fileUrl,
                            projectCode,
                            PRODUCT_TYPE.TECHNOLOGY_TRANSFER.short,
                            index + 1
                          )}
                          handleDownloadOrView={handleDownloadFile}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* training result */}
          <h4>{t('r05.b.b2.no3.title')}</h4>
          <div className="form-table p-fluid">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 200 }}>
                    {t('r05.b.b2.table.productName')}
                  </th>
                  <th className="text-center" style={{ minWidth: 100, width: 100 }}>
                    {t('r05.b.b2.table.kpiType')}
                  </th>
                  <th className="text-center" style={{ width: 200, maxWidth: 200 }}>
                    {t('r05.b.b2.table.kpiName')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 200 }}>
                    {t('r05.b.b2.table.productDetail')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 200 }}>
                    {t('r05.b.b2.table.proof')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {trainingResults.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-2">
                      {t('submitDocument.noProduct')}
                    </td>
                  </tr>
                )}
                {trainingResults?.map((item, index) => (
                  <tr key={item.customId}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item?.productDetail?.title}</td>
                    <td>{item?.researchKpiGroup?.name}</td>
                    <td>{item?.researchKpi?.name}</td>
                    <td
                      dangerouslySetInnerHTML={{
                        __html: getProductDetail(t, item?.productDetail),
                      }}
                    />
                    <td>
                      {item?.fileUrl && (
                        <DownloadOrViewPdf
                          opts={{ path: item?.fileUrl }}
                          fileName={formatFileNameProduct(
                            item?.fileUrl,
                            projectCode,
                            PRODUCT_TYPE.TRAINING_RESULT.short,
                            index + 1
                          )}
                          handleDownloadOrView={handleDownloadFile}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* summary report conference */}
          <h4>{t('r05.b.b3.title')}</h4>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 220, width: 240 }}>
                    {t('r05.b.b3.no1.name')}
                  </th>
                  <th className="text-center" style={{ minWidth: 80, width: 80 }}>
                    {t('r05.b.b3.no1.time')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 280 }}>
                    {t('r05.b.b3.no1.location')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: 280 }}>
                    {t('r05.b.b3.no1.result')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {summaryReportConferences.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-2">
                      {t('submitDocument.noConference')}
                    </td>
                  </tr>
                )}
                {summaryReportConferences?.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item.title}</td>
                    <td>{formatDate(item.organizedDate)}</td>
                    <td>{item.address}</td>
                    <td>{item.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* summary report participants */}
          <p className="mt-4">{t('r05.b.b3.no2.description')}</p>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 140, width: 180 }}>
                    {t('r05.b.b3.no2.name')}
                  </th>
                  <th className="text-center" style={{ minWidth: 80, width: 80 }}>
                    {t('r05.b.b3.no2.time')}
                  </th>
                  <th className="text-center" style={{ minWidth: 160, width: 220 }}>
                    {t('r05.b.b3.no2.location')}
                  </th>
                  <th className="text-center" style={{ minWidth: 160, width: 220 }}>
                    {t('r05.b.b3.no2.content')}
                  </th>
                  <th className="text-center" style={{ minWidth: 160, width: 220 }}>
                    {t('r05.b.b3.no2.result')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {summaryReportParticipants.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-2">
                      {t('submitDocument.noParticipant')}
                    </td>
                  </tr>
                )}
                {summaryReportParticipants?.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item.fullName}</td>
                    <td>{formatDate(item.joiningDate)}</td>
                    <td>{item.address}</td>
                    <td>{item.content}</td>
                    <td>{item.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* summary report expenditure */}
          <h4 className="mt-4 mb-1">{t('r05.b.b4.title')}</h4>
          <div className="form-table">
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: 440, width: 440 }}>{t('r05.b.b4.expenditure')}</th>
                  <th style={{ minWidth: 110, width: 160 }}>{t('r05.b.b4.amountOfMoney')}</th>
                  <th style={{ minWidth: 220, width: 440 }}>{t('r05.b.b4.note')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="font-bold">{t('r05.b.b4.expenditureFromVNU')}</td>
                  <td className="text-right">
                    {formatCurrency(
                      setExpenditureTableOfSummaryReport(
                        summaryReportExpenditures,
                        'Funding for the project is allocated by VNU',
                        'expenditure'
                      )
                    )}
                  </td>
                  <td>
                    {setExpenditureTableOfSummaryReport(
                      summaryReportExpenditures,
                      'Funding for the project is allocated by VNU',
                      'notes'
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="font-bold">{t('r05.b.b4.expenditureAllocated')}</td>
                  <td className="text-right">
                    {formatCurrency(
                      setExpenditureTableOfSummaryReport(
                        summaryReportExpenditures,
                        'Allocated expenditure',
                        'expenditure'
                      )
                    )}
                  </td>
                  <td>
                    {setExpenditureTableOfSummaryReport(
                      summaryReportExpenditures,
                      'Allocated expenditure',
                      'notes'
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className="font-bold mb-0">{t('r05.b.b4.expenditureUsed.description')}</p>
                    <p>{t('r05.b.b4.expenditureUsed.note')}</p>
                  </td>
                  <td className="text-right">
                    {formatCurrency(
                      setExpenditureTableOfSummaryReport(
                        summaryReportExpenditures,
                        'Used expenditure',
                        'expenditure'
                      )
                    )}
                  </td>
                  <td>
                    {setExpenditureTableOfSummaryReport(
                      summaryReportExpenditures,
                      'Used expenditure',
                      'notes'
                    )}
                  </td>
                </tr>
                {/* subExpenditure table */}
                <tr>
                  <td colSpan={3}>
                    <div className="form-table">
                      <table className="mb-1">
                        <thead>
                          <tr>
                            <th className="text-center" style={{ minWidth: 40, width: 40 }}>
                              {t('formLayout.numericalOrder')}
                            </th>
                            <th className="text-center" style={{ minWidth: 340, width: 340 }}>
                              {t('r05.b.b4.nameContentResolved')}
                            </th>
                            <th className="text-center" style={{ minWidth: 140, width: 140 }}>
                              {t('r05.b.b4.amountOfMoney')}
                            </th>
                            <th className="text-center" style={{ minWidth: 380, width: 380 }}>
                              {t('r05.b.b4.note')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {subSummaryReportExpenditures?.map((item, index) => (
                            <tr key={item?.id}>
                              <td className="text-center">{index + 1}</td>
                              <td>{item?.expenseName}</td>
                              <td className="text-right">{formatCurrency(item?.expenditure)}</td>
                              <td>{item?.notes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className="font-bold">{t('r05.b.b4.expenditurePropose')}</p>
                  </td>
                  <td className="text-right">
                    {formatCurrency(
                      setExpenditureTableOfSummaryReport(
                        summaryReportExpenditures,
                        'Suggested expenditures',
                        'expenditure'
                      )
                    )}
                  </td>
                  <td>
                    {setExpenditureTableOfSummaryReport(
                      summaryReportExpenditures,
                      'Suggested expenditures',
                      'notes'
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6 className="mb-0">{t('r05.b.b4.expenditureProof')}</h6>
          {renderDownloadExpenditureFile()}
          {/* B5 */}
          <div>
            <h4 className="my-2">{t('r05.b.b5.title')}</h4>
            <h6 className="my-2">{t('r05.b.b5.no1')}</h6>
            <RichTextViewer value={summaryReport?.summaryReport?.contentReview} disabled />
            <h6 className="mb-2 mt-4">{t('r05.b.b5.no2')}</h6>
            <RichTextViewer value={summaryReport?.summaryReport?.productReview} disabled />
            <h6 className="mb-2 mt-4">{t('r05.b.b5.no3')}</h6>
            <RichTextViewer value={summaryReport?.summaryReport?.processReview} disabled />
            <h6 className="mb-2 mt-4">{t('r05.b.b5.no4')}</h6>
            <RichTextViewer value={summaryReport?.summaryReport?.suggestion} disabled />
          </div>
          {/* R08 */}
          <h4 className="mt-4 mb-0">{t('r08.summaryReportFile')}</h4>
          {summaryReport?.summaryReport?.summaryReportFileUrl ? (
            <DownloadOrViewPdf
              opts={{ path: summaryReport?.summaryReport?.summaryReportFileUrl }}
              fileName={formatFileName(summaryReport?.summaryReport?.summaryReportFileUrl)}
              handleDownloadOrView={handleDownloadFile}
            />
          ) : (
            <span className="font-light font-italic">{t('r08.noFile')}</span>
          )}
          {/* Appendix */}
          <ManagementAppendix
            projectProposalId={projectData?.projectProposalId}
            project={projectData}
            attachment={summaryReport?.summaryReport?.attachment}
            isReview
          />

          <h4 className="mt-4 mb-0">{t('r08.proofAndAppendices')} </h4>
          <UploadMultipleFile
            disabled
            control={control}
            errors={errors}
            projectCode={summaryReport?.summaryReport?.projectCode}
            summaryReportAppendixeArray={summaryReportAppendixeArray}
            handleDownloadFile={handleDownloadFile}
            setSummaryReportAppendixeArray={setSummaryReportAppendixeArray}
          />
        </form>
      </div>
    </div>
  );
}

ReviewForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  summaryReport: PropTypes.object,
  projectId: PropTypes.string,
  setIsViewHistoryVisible: PropTypes.func,
};

ReviewForm.defaultProps = {
  summaryReport: {},
  projectId: '',
  setIsViewHistoryVisible: () => {},
};
